/**
 * What can be put on a diagram.
 *
 * A process is drawn out of TRANSACTION OBJECTS, so that is what the palette
 * offers. Steps and entities are the same kind of thing listed apart, using the
 * hint the metadata already carries — an object others look up and that looks
 * up nobody is master data — because that split is worth having before anything
 * is clicked. A DATA STORE is one of these too: an object the process writes
 * and nobody types into. It is not a separate source, it is a kind you give a
 * box in the panel on the right, which is why there is no third list here.
 *
 * Reports, dashboards, visuals, pages and general processes are real things a
 * diagram may want to point at, and they are behind a switch that starts off. A
 * list of everything in a project is a list nobody reads.
 */

import { useMemo, useState } from 'react'
import { Boxes, Search, Users, Workflow, UserRound, LayoutDashboard } from 'lucide-react'

import type { FlowPalette as Palette, NodeKind, PaletteObject } from '@/api/processflow'
import { KIND_STYLE } from './shapes'

interface Props {
  palette: Palette | null
  /** Object names / flow names already on the canvas. */
  placed: Set<string>
  onPlace: (spec: {
    kind: NodeKind
    obj_name: string
    obj_type: string
    label: string
    flow?: string
  }) => void
}

type GroupKey = 'step' | 'entity' | 'process'

export default function FlowPalette({ palette, placed, onPlace }: Props) {
  const [q, setQ] = useState('')
  const [showExtras, setShowExtras] = useState(false)
  const [open, setOpen] = useState<Record<string, boolean>>({
    step: true, entity: true, process: true, extras: true,
  })

  const needle = q.trim().toLowerCase()
  const hit = (...parts: Array<string | undefined>) =>
    !needle || parts.some((p) => (p || '').toLowerCase().includes(needle))

  const steps = useMemo(
    () => (palette?.objects || []).filter((o) => o.role_hint !== 'entity'
      && hit(o.label, o.obj_name)),
    [palette, needle])
  const entities = useMemo(
    () => (palette?.objects || []).filter((o) => o.role_hint === 'entity'
      && hit(o.label, o.obj_name)),
    [palette, needle])
  const flows = useMemo(
    () => (palette?.flows || []).filter((f) => hit(f.title, f.name)),
    [palette, needle])
  const extras = useMemo(
    () => (palette?.extras || []).filter((o) => hit(o.label, o.obj_name, o.type_label)),
    [palette, needle])

  const place = (o: PaletteObject, kind: NodeKind) => onPlace({
    kind, obj_name: o.obj_name, obj_type: o.obj_type, label: o.label,
  })

  return (
    <div style={{
      width: 236, flexShrink: 0, display: 'flex', flexDirection: 'column',
      borderRight: '1px solid var(--color-border)',
      background: 'var(--color-bg-secondary)', minHeight: 0,
    }}>
      <div style={{ padding: '10px 11px 8px', borderBottom: '1px solid var(--color-border)' }}>
        <div style={{ position: 'relative' }}>
          <Search size={13} style={{
            position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)',
            color: 'var(--color-text-secondary)',
          }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find a screen or process"
            style={{ ...field, paddingLeft: 26, fontSize: '0.8225rem' }}
          />
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 9px 16px' }}>
        <Group
          k="step" icon={<Boxes size={12} />} title="Steps" count={steps.length}
          open={open.step} onToggle={() => setOpen((o) => ({ ...o, step: !o.step }))}
          hint="Screens somebody opens and works through. Place one, then mark it an entity or a data store on the right if that is what it is.">
          {steps.map((o) => (
            <Entry key={o.obj_name} title={o.label} sub={o.obj_name} kind="step"
              used={placed.has(o.obj_name)} onClick={() => place(o, 'step')} />
          ))}
        </Group>

        <Group
          k="entity" icon={<UserRound size={12} />} title="Entities" count={entities.length}
          open={open.entity} onToggle={() => setOpen((o) => ({ ...o, entity: !o.entity }))}
          hint="Master data others look up — customers, suppliers, employees, items. Screens the process reads rather than steps through.">
          {entities.map((o) => (
            <Entry key={o.obj_name} title={o.label} sub={o.obj_name} kind="entity"
              used={placed.has(o.obj_name)} onClick={() => place(o, 'entity')} />
          ))}
          {entities.length === 0 && (
            <Empty>
              Nothing here looks like master data yet. Place a screen from Steps
              and set it to Entity on the right.
            </Empty>
          )}
        </Group>

        {flows.length > 0 && (
          <Group
            k="process" icon={<Workflow size={12} />} title="Other processes" count={flows.length}
            open={open.process} onToggle={() => setOpen((o) => ({ ...o, process: !o.process }))}
            hint="Hand the work over to a process drawn on its own diagram.">
            {flows.map((f) => (
              <Entry key={f.name} title={f.title} sub={f.name} kind="process"
                used={placed.has(f.name)}
                onClick={() => onPlace({
                  kind: 'process', obj_name: f.name, obj_type: 'L', label: f.title, flow: f.name,
                })} />
            ))}
          </Group>
        )}

        {/* Off by default: a diagram of a business is drawn out of its
            transactions, and everything else is an occasional reference. */}
        <label style={{
          display: 'flex', alignItems: 'center', gap: 6, marginTop: 14,
          fontSize: '0.8125rem', color: 'var(--color-text-secondary)', cursor: 'pointer',
        }}>
          <input type="checkbox" checked={showExtras} style={{ width: 'auto', margin: 0 }}
            onChange={(e) => setShowExtras(e.target.checked)} />
          <LayoutDashboard size={11} />
          Also show reports &amp; dashboards
        </label>

        {showExtras && (
          <div style={{ marginTop: 8 }}>
            <Group
              k="step" icon={<LayoutDashboard size={12} />} title="Reports & other objects"
              count={extras.length} open={open.extras}
              onToggle={() => setOpen((o) => ({ ...o, extras: !o.extras }))}
              hint="Point a step at a report, dashboard, visual, page or general process.">
              {extras.map((o) => (
                <Entry key={`${o.obj_type}:${o.obj_name}`} title={o.label}
                  sub={`${o.type_label} · ${o.obj_name}`} kind="step"
                  used={placed.has(o.obj_name)} onClick={() => place(o, 'step')} />
              ))}
              {extras.length === 0 && <Empty>Nothing of these kinds in this project.</Empty>}
            </Group>
          </div>
        )}

        <button
          type="button"
          onClick={() => onPlace({
            kind: 'external', obj_name: 'Outside party', obj_type: 'EXT',
            label: 'Outside party',
          })}
          style={{
            ...entryBtn, marginTop: 10, borderStyle: 'dashed',
            color: 'var(--color-text-secondary)',
          }}
        >
          <Users size={12} />
          <span>Add an outside party</span>
        </button>
      </div>
    </div>
  )
}

function Group({ k, icon, title, count, open, onToggle, hint, children }: {
  k: GroupKey
  icon: React.ReactNode
  title: string
  count: number
  open: boolean
  onToggle: () => void
  hint?: string
  children: React.ReactNode
}) {
  const st = KIND_STYLE[k]
  return (
    <div style={{ marginBottom: 12 }}>
      <button type="button" onClick={onToggle} style={{
        display: 'flex', alignItems: 'center', gap: 6, width: '100%', cursor: 'pointer',
        background: 'transparent', border: 0, padding: '2px 0 4px',
        color: 'var(--color-text)', fontSize: '0.8125rem', fontWeight: 700,
        letterSpacing: '0.05em', textTransform: 'uppercase',
      }}>
        <span style={{ color: st.stroke, display: 'inline-flex' }}>{icon}</span>
        {title}
        <span style={{
          fontWeight: 500, color: 'var(--color-text-secondary)', fontSize: '0.8125rem',
        }}>
          {count}
        </span>
        <span style={{ flex: 1 }} />
        <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>
          {open ? '▾' : '▸'}
        </span>
      </button>
      {open && hint && (
        <p style={{
          fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: '0 0 5px',
          lineHeight: 1.4,
        }}>
          {hint}
        </p>
      )}
      {open && children}
    </div>
  )
}

function Entry({ title, sub, kind, used, onClick }: {
  title: string
  sub: string
  kind: GroupKey
  used: boolean
  onClick: () => void
}) {
  const st = KIND_STYLE[kind]
  return (
    <button
      type="button" onClick={onClick} disabled={used}
      title={used ? 'Already on this diagram' : title}
      style={{
        ...entryBtn,
        borderLeft: `3px solid ${st.stroke}`,
        opacity: used ? 0.45 : 1,
        cursor: used ? 'default' : 'pointer',
      }}
    >
      <span style={{ minWidth: 0, textAlign: 'left', flex: 1 }}>
        <span style={ellipsis}>{title}</span>
        <span style={{ ...ellipsis, fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          {sub}
        </span>
      </span>
      {!used && <span style={{ color: 'var(--color-text-secondary)', fontSize: 14 }}>+</span>}
    </button>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <p style={{
      fontSize: '0.8125rem', color: 'var(--color-text-secondary)', lineHeight: 1.45,
      margin: '2px 0 0',
    }}>
      {children}
    </p>
  )
}

const ellipsis: React.CSSProperties = {
  display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}
const entryBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6, width: '100%',
  padding: '4px 6px', marginBottom: 3, borderRadius: 5,
  fontSize: '0.8125rem', border: '1px solid var(--color-border)',
  background: 'var(--color-bg-secondary)', color: 'var(--color-text)',
  cursor: 'pointer',
}
const field: React.CSSProperties = {
  width: '100%', padding: '0.35rem 0.5rem', borderRadius: 6, fontSize: '0.8625rem',
  border: '1px solid var(--color-border)',
  background: 'var(--color-input-bg, #fff)', color: 'var(--color-text)',
}
